import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { format as d3Format } from 'd3-format';
import { Row, Col } from 'reactstrap';
import { DateTime } from 'luxon';
import styled from 'styled-components';
import { useIntl } from 'react-intl';

import { i18nDecimalFormat } from 'src/util/i18n/handler';
import { getEnergyOriginLabel } from 'src/components/Property/PropertyShow/helpers/common';

import {
  DATA_AGGREGATE_BY_METER, DATA_GROUP_BY_TRADE_TYPE,
  DATA_AGGREGATE_BY_PROPERTY, DATA_GROUP_BY_COUNTERPARTY,
} from 'src/util/constants';
import {
  BUY, EXPORTS, IMPORTS, SELL, UNIT_CURRENCY_FORMAT, UNIT_ENERGY_FORMAT,
  UNIT_CARBON_DEFAULT_LABEL, UNIT_CARBON_TOOLTIP_FORMAT, UNIT_ENERGY_LABEL,
  METER, TRADE, UNIT_CURRENCY, UNIT_CURRENCY_TOOLTIP_FORMAT,
} from './chartConstants';
import { formatCarbonEmission, getTooltipPosition } from './ChartHelpers';
// Styles
const base = 2.5;

const TooltipContainer = styled.div`
width: max-content;
fontSize: ${base * 0.25}rem;
fontFamily: inherit;
color: #212529;
padding: ${base * 0.3}rem;
background-color: #fff;
border: ${base * 0.1}rem solid #f5f7fa;
border-radius: ${base * 0.1}rem;
`;

const MainHeader = styled.h5`
font-size: ${base * 0.4}rem;
margin-bottom: ${base * 0.1}rem;
`;

const SectionWrapper = styled.div`
font-size: ${base * 0.3}rem;
`;

const SectionHeader = styled.h6`
font-size: ${base * 0.3}rem;
margin-bottom: ${base * 0.1}rem;
`;

const SectionContainer = styled.div`
max-width: 200px;
margin-bottom: ${base * 0.2}rem;
`;

// End of styles

/**
 * Format the values(carbon/energy) in the tooltip
 * @param {boolean} isCarbon - flag to indicate carbon.
 * @param {string} format - the tooltip format.
 * @param {string} yAxisFormat
 * @param {number} value
 * @param {string} unitLabel
 * @returns {string} - formatted tooltip data.
 */
export const formatTooltipData = (isCarbon, format, yAxisFormat, value, unitLabel) => {
  if (isCarbon) {
    const finalValues = formatCarbonEmission(value);
    if (finalValues) {
      const { value: finalValue, label } = finalValues;
      return `${i18nDecimalFormat(d3Format(format)(finalValue))}${label}`;
    }
  }
  return `${i18nDecimalFormat(d3Format(yAxisFormat)(value))}${unitLabel}`;
};

/**
 * Forms the meter label for the tooltip
 * @param {string} dir - import or export
 * @param {string} title - meter title
 * @param {string} identifier - meter identifier
 * @param {import('react-intl').IntlShape} intl - meter identifier
 * @returns {React.ReactElement} - meter label.
 */
const getMeterLabel = (dir, title, identifier, intl) => {
  let meterDetails = title || '';
  meterDetails = identifier && `${title} (${identifier})`;
  const header = dir === 'Imports' ? intl.formatMessage({ id: 'common.entities.imports.label', defaultMessage: 'Imports' }) : intl.formatMessage({ id: 'common.entities.exports.label', defaultMessage: 'Exports' });
  return (
    <>
      {header}
      <>
        {meterDetails ? ` - ${meterDetails}` : ''}
        {' '}
      </>
    </>
  );
};

// child components rendering

/**
 * Renders the tooltip content for trade chart
 * @param {boolean} isCarbon
 * @param {object} data - the tooltip datapack.
 * @param {string} yAxisFormat
 * @param {string} unitLabel
 * @param {string} format - the tooltip format.
 * @param {object} chartView - { aggregateBy, groupBy }
 * @param {DATA_AGGREGATE_BY_PROPERTY | DATA_AGGREGATE_BY_METER} chartView.aggregateBy
 * @param {DATA_GROUP_BY_COUNTERPARTY | DATA_GROUP_BY_TRADE_TYPE} chartView.groupBy
 * @returns {React.ReactElement | null} - trade tooltip.
 */
export const tradeChartTooltip = (
  isCarbon,
  data,
  yAxisFormat,
  unitLabel,
  format,
  chartView,
) => {
  if (!data || !chartView) {
    return null;
  }
  const intl = useIntl();
  const tradeTooltip = [BUY, SELL].map((direction) => {
    if (!data[direction] || Object.keys(data[direction]).length === 0) {
      return null;
    }
    const tooltipLabel = direction === BUY ? intl.formatMessage({ id: 'common.entities.imports.label', defaultMessage: 'Imports' }) : intl.formatMessage({ id: 'common.entities.exports.label', defaultMessage: 'Exports' });

    const { aggregateBy } = chartView;
    const isByMeter = aggregateBy === DATA_AGGREGATE_BY_METER;

    return (
      <SectionWrapper key={direction}>
        {!isByMeter && (
          <SectionHeader>
            {tooltipLabel}
          </SectionHeader>
        )}

        {Object.keys(data[direction]).map((key) => {
          const d = data[direction][key];
          if (!d) {
            return null;
          }
          const {
            label, meter, subLabel, value,
          } = d;

          const { title, identifier } = meter || {};
          const meterLabel = getMeterLabel(tooltipLabel, title, identifier, intl);
          const energyOriginLabel = getEnergyOriginLabel(intl, label, chartView);

          return (
            <Fragment key={key}>
              {(isByMeter)
                && (
                  <SectionHeader>
                    {meterLabel}
                  </SectionHeader>
                )}
              <Row key={key} className="mb-1">
                <Col className="pe-0">
                  <span>{energyOriginLabel}</span>
                  {subLabel && (
                    <>
                      <br />
                      <small>
                        {`(${subLabel})`}
                      </small>
                    </>
                  )}
                </Col>
                <Col xs="auto">
                  {formatTooltipData(isCarbon, format, yAxisFormat, value, unitLabel)}
                </Col>
              </Row>

            </Fragment>
          );
        })}
      </SectionWrapper>
    );
  });
  return tradeTooltip;
};

/**
 * Forms the content for the meter tooltip
 * @param {object} tooltipData
 * @param {string} label
 * @param {boolean} isCarbon
 * @param {string} tooltipFormat
 * @param {string} yAxisFormat
 * @param {string} unitLabel
 * @param {import('react-intl').IntlShape} intl
 * @returns {React.ReactElement} - meter tooltip content.
 */
const meterContent = (
  tooltipData,
  label,
  isCarbon,
  tooltipFormat,
  yAxisFormat,
  unitLabel,
  intl,
) => {
  const {
    flags, value, title, identifier,
  } = tooltipData || {};
  return (
    <SectionWrapper>
      <SectionHeader>
        {getMeterLabel(label, title, identifier, intl)}
      </SectionHeader>
      <div>
        {formatTooltipData(isCarbon, tooltipFormat, yAxisFormat, value, unitLabel)}
      </div>
      {flags?.length > 0 && (
        <SectionContainer>
          {flags.map((el) => el.identifier).join(', ')}
        </SectionContainer>
      )}
    </SectionWrapper>
  );
};

const renderMeterDetails = (
  isCarbon,
  tooltipData,
  label,
  unitLabel,
  yAxisFormat,
  tooltipFormat,
  isByMeter,
  intl,
) => {
  if (!isByMeter) {
    return meterContent(tooltipData, label, isCarbon, tooltipFormat, yAxisFormat, unitLabel, intl);
  }

  const meterDetails = Object.keys(tooltipData).map((meterId) => (
    <Fragment key={meterId}>
      {meterContent(
        tooltipData[meterId],
        label,
        isCarbon,
        tooltipFormat,
        yAxisFormat,
        unitLabel,
        intl,
      )}
    </Fragment>
  ));
  return meterDetails;
};

/**
 * Renders the tooltip content for a meter chart.
 * @param {boolean} isCarbon - Indicates whether the chart is for carbon data.
 * @param {object} tooltipData - The data to be displayed in the tooltip.
 * @param {UNIT_ENERGY_FORMAT | UNIT_CURRENCY_FORMAT} yAxisFormat - The format for the
 * y-axis values.
 * @param {UNIT_CARBON_DEFAULT_LABEL | UNIT_ENERGY_LABEL | string} unitLabel - The label for
 * the unit of the data.
 * @param {UNIT_CARBON_TOOLTIP_FORMAT} tooltipFormat - The format for the tooltip values.
 * @param {object} chartView - { aggregateBy, groupBy }
 * @param {DATA_AGGREGATE_BY_PROPERTY | DATA_AGGREGATE_BY_METER} chartView.aggregateBy
 * @param {DATA_GROUP_BY_COUNTERPARTY | DATA_GROUP_BY_TRADE_TYPE} chartView.groupBy
 * @returns {React.ReactElement | null} - The rendered tooltip content,
 */
export const meterChartTooltip = (
  isCarbon,
  tooltipData,
  yAxisFormat,
  unitLabel,
  tooltipFormat,
  chartView,
) => {
  if (!tooltipData || !chartView) {
    return null;
  }
  const { tooltipConsumed, tooltipGenerated } = tooltipData;
  if (!tooltipConsumed && !tooltipGenerated) {
    return null;
  }
  const intl = useIntl();
  const { aggregateBy } = chartView;
  const isByMeter = aggregateBy === DATA_AGGREGATE_BY_METER;

  const tooltips = [
    {
      key: 'tooltip-consumed',
      tooltip: (tooltipConsumed
        && (renderMeterDetails(
          isCarbon,
          tooltipConsumed,
          IMPORTS,
          unitLabel,
          yAxisFormat,
          tooltipFormat,
          isByMeter,
          intl,
        ))
      ),
    }, {
      key: 'tooltip-generated',
      tooltip: (tooltipGenerated
        && (renderMeterDetails(
          isCarbon,
          tooltipGenerated,
          EXPORTS,
          unitLabel,
          yAxisFormat,
          tooltipFormat,
          isByMeter,
          intl,
        ))),
    },
  ];

  return (
    tooltips.map((tt) => (
      <Fragment key={tt.key}>
        {tt.tooltip}
      </Fragment>
    ))
  );
};

// End of child components rendering

/**
 * Description
 * @param {any} props
 * @returns {React.ReactElement} - ChartTooltip component
 */
function ChartTooltip(props) {
  if (!props) {
    return null;
  }
  const {
    axisFormat, chartType, chartWidth,
    chartView, datum, tooltipUpdate,
    tooltipProps, x,
  } = props;

  const {
    isCarbon,
    unit,
    unitLabel,
    tooltipDateFormat,
    tooltipData,
    tooltipFormat,
    tooltipTimestamp,
  } = tooltipProps || {};

  const yAxisFormat = unit === UNIT_CURRENCY
    ? UNIT_CURRENCY_TOOLTIP_FORMAT
    : axisFormat;

  if (!tooltipTimestamp) {
    return null;
  }

  const { ts: tooltipTimestampValue } = tooltipTimestamp; // Is required property.
  const { fullDateObj: datumTimestamp } = datum;

  if (datumTimestamp) {
    const { ts: datumTimestampValue } = datumTimestamp;

    // This happens when the tooltip is trigerred from the whitespace above the bars.
    // We need to update the parent component with the correct timestamp to get the tooltip data
    if (datumTimestampValue && datumTimestampValue !== tooltipTimestampValue) {
      tooltipUpdate(datumTimestamp);
    }
  }

  return (
    <g>
      <foreignObject x={getTooltipPosition(x, chartWidth)} width="1" height="100%" className="overflow-visible">
        <TooltipContainer className={`${chartType}-tooltip`}>
          <MainHeader>{tooltipTimestamp.toLocaleString(tooltipDateFormat)}</MainHeader>
          {chartType === 'trade'
            ? tradeChartTooltip(
              isCarbon,
              tooltipData,
              yAxisFormat,
              unitLabel,
              tooltipFormat,
              chartView,
            )
            : meterChartTooltip(
              isCarbon,
              tooltipData,
              yAxisFormat,
              unitLabel,
              tooltipFormat,
              chartView,
            )}
        </TooltipContainer>
      </foreignObject>
    </g>
  );
}

export default ChartTooltip;

ChartTooltip.propTypes = {
  axisFormat: PropTypes.string.isRequired,
  chartView: PropTypes.shape({
    groupBy: PropTypes.oneOf([
      DATA_GROUP_BY_COUNTERPARTY,
      DATA_GROUP_BY_TRADE_TYPE,
    ]).isRequired,
    aggregateBy: PropTypes.oneOf([
      DATA_AGGREGATE_BY_METER,
      DATA_AGGREGATE_BY_PROPERTY,
    ]).isRequired,
  }).isRequired,
  chartType: PropTypes.oneOf([METER, TRADE]).isRequired,
  chartWidth: PropTypes.number.isRequired,
  datum: PropTypes.oneOfType([PropTypes.object]),
  tooltipProps: PropTypes.shape({
    isCarbon: PropTypes.bool.isRequired,
    unit: PropTypes.string.isRequired,
    unitLabel: PropTypes.string.isRequired,
    tooltipDateFormat: PropTypes.oneOfType([PropTypes.object]).isRequired,
    tooltipData: PropTypes.oneOfType([PropTypes.object]).isRequired,
    tooltipFormat: PropTypes.string,
    tooltipLabelFunc: PropTypes.func.isRequired,
    tooltipTimestamp: PropTypes.instanceOf(DateTime),
  }),
  tooltipUpdate: PropTypes.func.isRequired,
  x: PropTypes.number,
};
ChartTooltip.defaultProps = {
  datum: undefined,
  tooltipProps: {
    tooltipFormat: null,
    tooltipTimestamp: undefined,
  },
  x: undefined,
};
